import React, { useEffect, useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import { useDispatch, useSelector } from 'react-redux';

import ImageUpload from '@components/ImageUpload';
import { toastError } from '@components/Toast';

import { closeLoader, openLoader } from '@store/actions/LoaderAction';

import ApiClient from '@lib/apiClient';

import { checkFileExtention, getFileType, getIconByFileType } from '@utils/Util';

import { BASE_URL } from '@/constants/constant';

const AddCertificates = ({ isShow, handleClose, id, selectedCertificate }) => {
	const [selectedItem, setSelectedItem] = useState(null);
	const [errors, setErrors] = useState({});
	const [certificateTypes, setCertificateTypes] = useState([]);
	const dispatch = useDispatch();
	const loader = useSelector((store) => store.loaderStore);

	const getCertificateTypes = async () => {
		try {
			const params = 'info.nitex.app.supplier.CertificateType';
			dispatch(openLoader());
			const { data } = await ApiClient.GET('getEnums', params);
			setCertificateTypes(data);
			dispatch(closeLoader());
		} catch (error) {
			toastError(error.message);
			dispatch(closeLoader());
		}
	};

	useEffect(() => {
		if (selectedCertificate) {
			setSelectedItem({
				certificateType: selectedCertificate?.certificateType,
				certificateTypeName: selectedCertificate?.certificateTypeName,
				docResponseId: selectedCertificate?.documentResponse?.id,
				documentDTO: {
					documentType: selectedCertificate?.documentResponse?.docType,
					docUrl: selectedCertificate?.documentResponse?.docUrl,
					id: selectedCertificate?.documentResponse?.id,
					name: selectedCertificate?.documentResponse?.name
				},

				expireDate: selectedCertificate?.expireDate,
				id: selectedCertificate?.id
			});
		}
	}, [selectedCertificate]);

	useEffect(() => {
		getCertificateTypes();
	}, []);

	const renderFileDummyImage = (type) => {
		let fileType;
		if (type.docUrl) {
			fileType = getFileType(type.docUrl);
		} else {
			fileType = getFileType(type.name);
		}
		if (fileType === 'IMAGE' || fileType === 'NO_FILE') return <img src={type.docUrl} alt="Upload" />;
		else
			return (
				<img
					className="cursor-pointer file-icon"
					src={getIconByFileType(fileType)}
					onClick={() => {
						if (type.docUrl) {
							window.open(type.docUrl, '_blank').focus();
						}
					}}
					alt="certificate-image"
				/>
			);
	};

	const renderPreview = (type, previewType) => {
		const fileType = checkFileExtention(type);
		if (fileType) {
			return (
				<>
					{renderFileDummyImage(type)}
					<ImageUpload
						size="sm"
						onUploadImage={onUploadImage}
						docType={previewType === 'certificate' ? 'SUPPLIER_CERTIFICATE' : 'BADGE_DOCUMENT'}
						type={'imageDocument'}
						isEditable={true}
					/>
				</>
			);
		} else
			return (
				<>
					<img className="border-0" src={type.base64Str ? type.base64Str : type.docUrl} alt="" />
					<ImageUpload
						size="sm"
						onUploadImage={onUploadImage}
						docType={previewType === 'certificate' ? 'SUPPLIER_CERTIFICATE' : 'BADGE_DOCUMENT'}
						type={'imageDocument'}
						isEditable={true}
					/>
				</>
			);
	};

	const onUploadImage = (image) => {
		setSelectedItem({ ...selectedItem, documentDTO: image });
	};

	const isValidForm = (body) => {
		const errorsObj = {};
		if (!body?.certificateType) {
			errorsObj['certificateTypeError'] = 'Certificate type is required';
		}
		if (!body.expireDate) {
			errorsObj['expireDateError'] = 'Expiry date is required';
		}

		if (!body?.documentDTO && !selectedItem?.docResponseId) {
			errorsObj['imageError'] = 'Photo is required';
		}
		setErrors(errorsObj);
		return Object.keys(errorsObj).length === 0;
	};

	const onSubmit = async () => {
		let body = {};
		if (selectedItem?.documentDTO?.base64Str) {
			body = { ...selectedItem };
		} else {
			body = {
				certificateType: selectedItem?.certificateType,
				expireDate: selectedItem?.expireDate,
				id: selectedItem?.id
			};
		}
		if (!selectedItem?.documentDTO?.base64Str) {
			body['documentDTO'] = null;
		}
		if (isValidForm(body)) {
			try {
				dispatch(openLoader());
				if (selectedCertificate) {
					await ApiClient.PUT(
						`${BASE_URL}/brand/${id}/certificates/${selectedItem.id}`,
						body,
						'',
						true
					);
				} else {
					await ApiClient.POST(`${BASE_URL}/brand/${id}/certificates`, body, '', true);
				}
				handleClose(true);
			} catch (error) {
				toastError(error.message);
				dispatch(closeLoader());
			}
		}
	};

	return (
		<Modal
			show={isShow}
			onHide={handleClose}
			backdrop="static"
			centered
			className="width-784-dialog brand-certificate-crm"
		>
			<div className="common-popup width-784 crm-brand-certificate">
				<Modal.Header closeButton>
					<Modal.Title>
						<div className="popup-tilte">
							<h3 className="semibold-16 mb-0">Certificates</h3>
						</div>
					</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<div className="common-popup-body">
						<div className="container px-0">
							<div className="row">
								<div className="col-md-3">
									<div className="upload-section">
										{selectedItem?.documentDTO ? (
											renderPreview(selectedItem?.documentDTO, 'certificate')
										) : (
											<ImageUpload
												size="sm"
												onUploadImage={onUploadImage}
												docType={'SUPPLIER_CERTIFICATE'}
												type={'imageDocument'}
											/>
										)}
									</div>
									{errors.imageError && <p className="error">{errors.imageError}</p>}
								</div>
								<div className="col-md-9">
									<div className="form-group ">
										<label htmlFor="certificateType">Certificates*</label>
										<select
											className="w-100"
											name="certificateType"
											id="certificateType"
											value={selectedItem?.certificateType}
											onChange={(e) =>
												setSelectedItem({
													...selectedItem,
													certificateType: e.target.value
												})
											}
										>
											<option value={''}>Select</option>
											{certificateTypes?.map((item, idx) => (
												<option value={item.constant} key={idx}>
													{item.name}
												</option>
											))}
										</select>
										{errors.certificateTypeError && (
											<p className="error">{errors.certificateTypeError}</p>
										)}
									</div>
									<div className="form-group mb-0">
										<label htmlFor="expire-date">Expire date*</label>
										<input
											type="date"
											id="expire-date"
											value={selectedItem?.expireDate || ''}
											onChange={(e) =>
												setSelectedItem({
													...selectedItem,
													expireDate: e.target.value
												})
											}
										/>
										{errors.expireDateError && (
											<p className="error">{errors.expireDateError}</p>
										)}
									</div>
								</div>
							</div>
						</div>
					</div>
				</Modal.Body>
				<Modal.Footer>
					<div className="common-popup-footer">
						<div className="footer-buttons text-right">
							<button className="button outline size32" onClick={handleClose}>
								Cancel
							</button>
							<button disabled={loader} className="button size32 ml-2" onClick={onSubmit}>
								Submit
							</button>
						</div>
					</div>
				</Modal.Footer>
			</div>
		</Modal>
	);
};

export default AddCertificates;
